import React, { createContext, useContext, useEffect, ReactNode, useMemo, useCallback, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../store';
import { fetchUserPermissions, getCurrentUser } from '../store/slices/authSlice';
import { useTokenRefresh } from '../hooks/useTokenRefresh';
import type { Permission, User, Role } from '../types';
import { AdvancedPolicyEngine, type PolicyContext } from '../policies/policyEngine';
import RBACConfigManager from '../config/rbacConfig';
import { isAdmin as checkIsAdmin, hasRoleSlug, getUserRoleSlug } from '../utils/permissions';

interface RBACContextType {
  user: User | null;
  permissions: Permission[];
  isLoading: boolean;
  hasRole: (role: string) => boolean;
  hasPermission: (resource: string, action: string, scope?: string) => boolean;
  hasAnyPermission: (permissions: { resource: string; action: string; scope?: string }[]) => boolean;
  hasAllPermissions: (permissions: { resource: string; action: string; scope?: string }[]) => boolean;
  refreshPermissions: () => void;
  isAdmin: boolean;
  canAccess: (resource: string, action: string, scope?: string) => boolean;
  // RBAC v2: Multi-role data
  userRoles: Role[];
}

const RBACContext = createContext<RBACContextType | undefined>(undefined);

interface RBACProviderProps {
  children: ReactNode;
}

export const RBACProvider: React.FC<RBACProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const {
    user,
    permissions,
    isAuthenticated,
    isLoading,
    lastPermissionUpdate,
  } = useAppSelector((state) => state.auth);

  // Enable automatic token refresh
  useTokenRefresh();

  // Track if we've already fetched permissions for this session
  const hasFetchedRef = useRef(false);

  // Refresh permissions when user logs in or periodically
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const permissionsEmpty = !permissions || permissions.length === 0;
      const permissionsStale = !lastPermissionUpdate || Date.now() - lastPermissionUpdate > 5 * 60 * 1000; // 5 minutes

      if ((permissionsEmpty || permissionsStale) && !hasFetchedRef.current) {
        hasFetchedRef.current = true;
        dispatch(getCurrentUser());
        dispatch(fetchUserPermissions());
      }
    } else {
      hasFetchedRef.current = false;
    }
  }, [isAuthenticated, user?.id, permissions, lastPermissionUpdate, dispatch]);

  // Check if user has a specific role — supports multi-role (RBAC v2) and legacy single role
  const hasRole = useCallback((role: string): boolean => {
    if (!user) return false;
    return hasRoleSlug(user, role);
  }, [user]);

  // Check if user is admin
  const isAdmin = useMemo(() => {
    return checkIsAdmin(user);
  }, [user]);

  // RBAC v2: Derive role data from user object
  const userRoles: Role[] = useMemo(() => user?.roles ?? [], [user?.roles]);

  // Cache for permission results to avoid repeated evaluations
  const permissionCache = useRef<Map<string, { result: boolean; timestamp: number }>>(new Map());

  // Track last known permissions update timestamp and role to clear cache when they change
  const lastPermissionUpdateRef = useRef<number>(0);
  const lastRoleRef = useRef<string | undefined>(undefined);

  const userRoleSlug = getUserRoleSlug(user);

  // Clear cache when permissions or role change
  useEffect(() => {
    const currentPermissionUpdate = lastPermissionUpdate ?? 0;

    const permissionsChanged = currentPermissionUpdate !== lastPermissionUpdateRef.current;
    const roleChanged = userRoleSlug !== lastRoleRef.current;

    if (permissionsChanged || roleChanged) {
      permissionCache.current.clear();
      lastPermissionUpdateRef.current = currentPermissionUpdate;
      lastRoleRef.current = userRoleSlug;
    }
  }, [lastPermissionUpdate, userRoleSlug]);

  // Check if user has a specific permission using policy engine
  const hasPermission = useCallback((resource: string, action: string, scope?: string): boolean => {
    // If still loading auth state, defer access decisions
    if (isLoading) {
      return true; // Optimistically allow during loading to prevent flicker
    }

    // Create cache key
    const cacheKey = `${resource}:${action}:${scope || 'none'}`;
    const now = Date.now();

    // Check cache first (cache for 1 second to avoid excessive evaluations)
    const cached = permissionCache.current.get(cacheKey);
    if (cached && (now - cached.timestamp) < 1000 && lastPermissionUpdate) {
      return cached.result;
    }

    // Use policy engine if enabled, otherwise fall back to basic check
    const config = RBACConfigManager.getConfig();

    if (config.policies.enabled && user) {
      const policyContext: PolicyContext = {
        resource,
        action,
        scope,
        timestamp: new Date(Math.floor(Date.now() / 1000) * 1000)
      };

      const evaluation = AdvancedPolicyEngine.evaluateAccess(user, permissions, policyContext);

      permissionCache.current.set(cacheKey, { result: evaluation.allowed, timestamp: now });

      if (config.audit.logAccess && (!cached || (now - cached.timestamp) > 5000)) {
        console.log(`Access check: ${resource}:${action}:${scope || 'none'} - ${evaluation.allowed ? 'ALLOWED' : 'DENIED'}`);
      }

      return evaluation.allowed;
    }

    // Fallback to basic permission check
    if (isAdmin) {
      return true;
    }

    if (!permissions || permissions.length === 0) {
      return false;
    }

    return permissions.some(permission => {
      const resourceMatch = permission.resource === resource;
      const actionMatch = permission.action === action;
      const scopeMatch = !scope || permission.scope === scope || permission.scope === 'global';

      return resourceMatch && actionMatch && scopeMatch;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, userRoleSlug, permissions, isAdmin, isLoading, lastPermissionUpdate]);

  // Memoized permission array checks
  const hasAnyPermission = useCallback((permissionList: { resource: string; action: string; scope?: string }[]): boolean => {
    return permissionList.some(perm => hasPermission(perm.resource, perm.action, perm.scope));
  }, [hasPermission]);

  const hasAllPermissions = useCallback((permissionList: { resource: string; action: string; scope?: string }[]): boolean => {
    return permissionList.every(perm => hasPermission(perm.resource, perm.action, perm.scope));
  }, [hasPermission]);

  // Alias for hasPermission for better readability
  const canAccess = hasPermission;

  // Memoized refresh permissions function
  const refreshPermissions = useCallback(() => {
    if (isAuthenticated) {
      dispatch(fetchUserPermissions());
    }
  }, [isAuthenticated, dispatch]);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue: RBACContextType = useMemo(() => ({
    user,
    permissions,
    isLoading,
    hasRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions,
    isAdmin,
    canAccess,
    userRoles,
  }), [
    user?.id,
    userRoleSlug,
    permissions,
    isLoading,
    isAdmin,
    userRoles,
    lastPermissionUpdate,
    hasPermission,
  ]);

  return (
    <RBACContext.Provider value={contextValue}>
      {children}
    </RBACContext.Provider>
  );
};

// Custom hook to use RBAC context
export const useRBAC = (): RBACContextType => {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
};

// Higher-order component for role-based rendering
export const withRole = <P extends object>(
  Component: React.ComponentType<P>,
  requiredRole: string
) => {
  return (props: P) => {
    const { hasRole } = useRBAC();

    if (!hasRole(requiredRole)) {
      return null;
    }

    return <Component {...props} />;
  };
};

// Higher-order component for permission-based rendering
export const withPermission = <P extends object>(
  Component: React.ComponentType<P>,
  resource: string,
  action: string,
  scope?: string
) => {
  return (props: P) => {
    const { hasPermission } = useRBAC();

    if (!hasPermission(resource, action, scope)) {
      return null;
    }

    return <Component {...props} />;
  };
};

export default RBACContext;
